import Select, { type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { logError } from '@databases/logs';
import { defaultStoreUtils } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type Database,
  type EntryWithContext,
  type EntryCreateOptions,
  type BrokenDatabase,
  type DatabaseField
} from '@databases/types/database';
import {
  getDatabaseEntries,
  getEntryName,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n.ts';

import EntryPickerOption from './DatabaseEntryPickerOption.tsx';

type Option = {
  label: string;
  value: string;
  data: EntryWithContext | EntryCreateOptions;
};

export const isCreate = (
  val: EntryWithContext | EntryCreateOptions | string | null | undefined
): val is EntryCreateOptions =>
  !!val && typeof val !== 'string' && !('id' in val) && !('entry' in val);

export const getOptionValue = (value: EntryWithContext | EntryCreateOptions) =>
  isCreate(value) ? '' : value.entry ? value.entry.id : '';

export interface DatabaseEntryPickerProps {
  selectedDatabase: Database | BrokenDatabase | undefined;
  setSelectedEntry: (value: EntryWithContext | EntryCreateOptions | undefined) => void;
  selectedEntry: EntryWithContext | EntryCreateOptions | undefined;
  setSelectedField: (value: DatabaseField | undefined) => void;
  canCreate: boolean;
}

const DatabaseEntryPicker: FunctionComponent<DatabaseEntryPickerProps> = ({
  selectedDatabase,
  setSelectedEntry,
  selectedEntry,
  setSelectedField,
  canCreate
}) => {
  const store = defaultStoreUtils.useStore();
  const { formatMessage } = useIntl();

  const emptyObj = Object.freeze({});

  const getOptionLabel = useCallback(
    (value: EntryWithContext | EntryCreateOptions) => {
      try {
        if (isCreate(value)) {
          return formatMessage(messages['reference-entry-picker-option.add-icon-label']);
        } else {
          if (value) {
            const entryName = getEntryName(value, store.getState());

            return entryName
              ? entryName
              : formatMessage(messages['reference-entry-picker-option.unnamed-entry']);
          }
        }

        return '';
      } catch (error: unknown) {
        if (error instanceof Error) {
          currentSnackbarState.setMessage(error.message, {
            showCloseButton: true
          });
          logError(error);
        }
        return '';
      }
    },
    [formatMessage, store]
  );

  const formatOptionLabel = useCallback(
    (
      value: EntryWithContext | EntryCreateOptions,
      meta: FormatOptionLabelMeta<EntryWithContext | EntryCreateOptions>
    ): React.ReactNode => {
      const label = getOptionLabel(value);

      if (meta.context === 'menu') {
        return <EntryPickerOption isCreate={isCreate(value)} label={label} />;
      } else {
        return label;
      }
    },
    [getOptionLabel]
  );

  const resolvedOptions: Array<EntryWithContext | EntryCreateOptions> = useMemo(() => {
    if (!selectedDatabase || isBrokenDatabase(selectedDatabase)) {
      return [];
    }
    return [
      ...(canCreate ? [emptyObj] : []),
      ...getDatabaseEntries(selectedDatabase).map((entry) => ({
        database: selectedDatabase,
        entry
      }))
    ];
  }, [emptyObj, canCreate, selectedDatabase]);

  const handleChange = useCallback(
    (value: EntryWithContext | EntryCreateOptions | null) => {
      setSelectedEntry(value ?? undefined);
      setSelectedField(undefined);
    },
    [setSelectedEntry, setSelectedField]
  );

  const filterOption = useCallback(
    (option: Option, inputValue: string) => {
      return getOptionLabel(option.data).toLowerCase().includes(inputValue.toLowerCase());
    },
    [getOptionLabel]
  );

  return (
    <Select<EntryWithContext | EntryCreateOptions>
      classNamePrefix="database-entry-picker"
      options={resolvedOptions}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      value={selectedEntry ?? null}
      getOptionValue={getOptionValue}
      onChange={handleChange}
      placeholder={formatMessage(
        messages['reference-entry-picker.entry-optional-placeholder']
      )}
      noOptionsMessage={() =>
        formatMessage(messages['reference-entry-picker.no-options'])
      }
      isClearable={true}
    />
  );
};

export default DatabaseEntryPicker;
